import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';
import { Customer, CustomerRelationshipLog } from '../../types/customer';
import { 
  ExclamationCircleIcon, 
  CheckCircleIcon,
  PhoneIcon, 
  EnvelopeIcon, 
  BuildingOfficeIcon, 
  UserGroupIcon, 
  ArchiveBoxIcon 
} from '@heroicons/react/24/outline';

interface EditRelationshipLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  logToEdit: CustomerRelationshipLog | null;
  customer: Customer;
}

const EditRelationshipLogModal: React.FC<EditRelationshipLogModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  logToEdit,
  customer
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [content, setContent] = useState<string>('');
  const [contactType, setContactType] = useState<'phone' | 'email' | 'distributor' | 'internal' | 'postal'>('phone');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isOpenTask, setIsOpenTask] = useState<boolean>(false);

  // Reset form and populate fields when modal opens or logToEdit changes
  useEffect(() => {
    if (isOpen && logToEdit) {
      setContent(logToEdit.content);
      setContactType(logToEdit.contactType || 'phone');
      setIsOpenTask(logToEdit.isOpen);
      setError('');
    }
  }, [isOpen, logToEdit]);

  const maxLength = 1000;

  // Kapcsolattípus opciók
  const contactTypeOptions = [
    {
      value: 'phone' as const,
      label: 'Telefon',
      icon: PhoneIcon,
      color: 'text-blue-600 bg-blue-50 border-blue-200'
    },
    {
      value: 'email' as const,
      label: 'Email',
      icon: EnvelopeIcon,
      color: 'text-green-600 bg-green-50 border-green-200'
    },
    {
      value: 'distributor' as const,
      label: 'Elosztói',
      icon: BuildingOfficeIcon,
      color: 'text-orange-600 bg-orange-50 border-orange-200'
    },
    {
      value: 'internal' as const,
      label: 'Belső',
      icon: UserGroupIcon,
      color: 'text-purple-600 bg-purple-50 border-purple-200'
    },
    {
      value: 'postal' as const,
      label: 'Postai',
      icon: ArchiveBoxIcon,
      color: 'text-gray-600 bg-gray-50 border-gray-200'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim()) {
      setError(t('customers.relationshipLog.contentRequired') || 'Content is required');
      return;
    }
    
    if (content.length > maxLength) {
      setError(t('customers.relationshipLog.maxLength') || `Maximum ${maxLength} characters`);
      return;
    }

    if (!user || !logToEdit) {
      setError(t('common.messages.authContext.error') || 'Authentication error');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Make sure we use the correct customer ID from the logToEdit
      const customerId = logToEdit.customerId;
      const customerRef = doc(db, 'customers', customerId);
      
      // Get the current customer document to work with the latest data
      const customerDocRef = doc(db, 'customers', customerId);
      const customerSnapshot = await getDoc(customerDocRef);
      if (!customerSnapshot.exists) {
        throw new Error('Customer not found');
      }
      
      const customerData = customerSnapshot.data() as Customer;
      const currentLogs = customerData.relationshipLogs || [];
      
      // Find the index of the log we're editing
      const logIndex = currentLogs.findIndex(log => log.id === logToEdit.id);
      if (logIndex === -1) {
        throw new Error('Log entry not found in customer data');
      }
      
      // Create a new logs array with the updated log
      const updatedLogs = [...currentLogs];
      updatedLogs[logIndex] = {
        ...currentLogs[logIndex],
        content, // Update the content
        contactType, // Update the contact type
        isOpen: isOpenTask // Update the isOpen status
      };
      
      // Update the entire logs array at once
      await updateDoc(customerRef, {
        relationshipLogs: updatedLogs
      });
      
      toast.success(t('customers.relationshipLog.editSuccess') || 'Log entry updated successfully');
      
      // Reset form and close modal
      setContent('');
      setContactType('phone');
      await onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to update log entry:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      toast.error(
        `${t('customers.relationshipLog.editError') || 'Failed to update log entry'}: ${errorMessage}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('customers.relationshipLog.edit') || 'Edit log entry'}
      size="xl"
    >
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          {/* Entry ID display */}
          {logToEdit && (
            <div className="mb-6">
              <label className="block text-base font-medium text-gray-700 mb-2">
                {t('customers.relationshipLog.entryId') || 'Entry ID'}
              </label>
              <div className="text-base bg-gray-100 p-3 rounded font-mono">
                {logToEdit.entryId}
              </div>
            </div>
          )}
          
          {/* Kapcsolattípus választó */}
          <div className="mb-6">
            <label className="block text-base font-medium text-gray-700 mb-3">
              {t('customers.relationshipLog.contactType.title') || 'Kapcsolattartás típusa'}
            </label>
            <div className="grid grid-cols-5 gap-2">
              {contactTypeOptions.map((option) => {
                const IconComponent = option.icon;
                const isSelected = contactType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setContactType(option.value)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      isSelected 
                        ? option.color 
                        : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'
                    }`}
                    disabled={isSubmitting}
                  >
                    <IconComponent className="h-5 w-5 mx-auto mb-1" />
                    <div className="text-xs font-medium">
                      {option.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Content textarea */}
          <div className="mb-6">
            <label htmlFor="log-content" className="block text-base font-medium text-gray-700 mb-2">
              {t('customers.relationshipLog.content') || 'Content'} <span className="text-red-500">*</span>
            </label>
            <textarea
              id="log-content"
              rows={8}
              className={`w-full rounded-lg border ${
                error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
              } shadow-sm p-4 text-base`}
              placeholder={t('customers.relationshipLog.contentPlaceholder') || 'Enter log content...'}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={maxLength}
              disabled={isSubmitting}
            />
            <div className="flex justify-between mt-2">
              {error ? (
                <p className="text-sm text-red-600">{error}</p>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('customers.relationshipLog.maxLength') || `Maximum ${maxLength} characters`}
                </p>
              )}
              <p className="text-sm text-gray-500">
                {content.length}/{maxLength}
              </p>
            </div>
          </div>
          
          {/* isOpen checkbox */}
          <div className="mb-6">
            <div className="flex items-center">
              <input
                id="isOpenTask"
                name="isOpenTask"
                type="checkbox"
                className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={isOpenTask}
                onChange={(e) => setIsOpenTask(e.target.checked)}
                disabled={isSubmitting}
              />
              <label htmlFor="isOpenTask" className="ml-3 flex items-center text-base font-medium text-gray-700">
                {isOpenTask ? (
                  <>
                    <ExclamationCircleIcon className="h-5 w-5 text-amber-500 mr-2" />
                    {t('customers.relationshipLog.hasOpenTasks') || 'Has pending tasks'}
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    {t('customers.relationshipLog.noOpenTasks') || 'No pending tasks'}
                  </>
                )}
              </label>
            </div>
            <p className="mt-1 ml-8 text-sm text-gray-500">
              {isOpenTask 
                ? t('customers.relationshipLog.pendingTaskDescription') || 'There are still pending tasks for this log entry'
                : t('customers.relationshipLog.completedTaskDescription') || 'All tasks for this log entry are complete'}
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end space-x-4 mt-8">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isSubmitting}
            >
              {t('customers.relationshipLog.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              className="px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t('common.submitting') || 'Saving...'
                : t('customers.relationshipLog.update') || 'Update entry'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditRelationshipLogModal; 